import { PricingRowVM } from './vetcor-pricing-row.model'
import { VetcorPricingRowService } from './vetcor-pricing-row.service'
import { Observable } from 'rxjs'
import {
   Component,
   Input,
   Output,
   EventEmitter,
   HostBinding,
   Injectable,
   OnInit,
   ViewChild,
   ElementRef,
   ChangeDetectorRef,
   ViewChildren,
   QueryList,
   signal,
   effect,
   input,
   computed,
   inject,
   model,
} from '@angular/core'
import { MatSort, Sort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { ToastrService } from 'ngx-toastr'
import { BehaviorSubject } from 'rxjs'
import { AppPractice, PricingManagerClient, ChangeType, HospitalPrice } from 'src/app/shared/services/ppv.api'
import { ComponentStore } from '@ngrx/component-store'
import { ChartComponent } from 'ng-apexcharts'
import { VetcorPricingNewCodeAdminComponent } from '../vetcor-pricing-new-code-admin/vetcor-pricing-new-code-admin.component'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { VetcorPricingRowComponent } from './vetcor-pricing-row/vetcor-pricing-row.component'
import { HospitalPricingService } from '../hospital-pricing.service'

@Injectable()
export class VetcorPricingTableComponentStore extends ComponentStore<{
   searchText: string
   searchCode: string
   selectedCategory: string
}> {
   constructor() {
      super({
         searchText: '',
         searchCode: '',
         selectedCategory: '',
      })
   }
}

@Component({
   selector: 'vetcor-pricing-table',
   templateUrl: './vetcor-pricing-table.component.html',
   styleUrls: ['./vetcor-pricing-table.component.scss'],
   providers: [VetcorPricingTableComponentStore],
   standalone: false,
})
export class VetcorPricingTableComponent implements OnInit {
   pricingService = inject(HospitalPricingService)

   @Output() unreviewedProducts = new EventEmitter<any>()
   @Output() recordUpdated = new EventEmitter<any>()
   @Output() productListSaved = new EventEmitter<boolean>()
   @Input() isCaptain: boolean = false //this is true when it's the admin page, false otherwise
   @Input() seeProductList: boolean = false
   @Input() editablePriceField!: 'newPrice' | 'opsPrice'
   @Input() isReadOnly: boolean = false
   @Input() isSubmitted: boolean = false
   @Input() isRequestView = false

   @Input() showUpdatePricingCaptain: boolean = false

   sortModel = signal<Sort>(undefined)
   siteCount = computed(() => this.sites()?.length)
   showPracticeColumn = signal<boolean>(true)

   chartYears = [new Date().getFullYear(), new Date().getFullYear() - 1]
   headerOffset: number = 0
   savedList: boolean = false
   loading = computed(() => !this.prices())
   practicesLookup$ = new BehaviorSubject<{
      [practiceSKey: number]: AppPractice
   }>(undefined)
   practices: AppPractice[] = []

   selectedCategory = ''
   dataSource: MatTableDataSource<PricingManagerClient> = new MatTableDataSource()
   @ViewChild(MatSort) sort!: MatSort
   topLevelRows = this.pricingTableService.topLevelRows
   public filteredProducts: Array<any> = []

   pendingReview = model<boolean>(false)

   @ViewChild('rationaleInput') rationaleInput: ElementRef
   approveAllPrices = true

   prices = input<HospitalPrice[]>(undefined)
   public pricingCaptain: Array<any> = []
   public codeFilterValue = model<string>('')
   public productNameFilter = model<string>('')
   public productTypeFilter = model<string>('')
   public productCategoryFilter = model<string>('')
   public productPracticeFilter = model<string>()
   public selectedSite = model<string>('')
   totalSuggestedPrice: any

   filteredTopLevelRows = computed(() => this.filterProducts())
   sortedRows = computed(() => this.sortData([...this.filteredTopLevelRows()]))

   writebackStatusFilter = model<number | null>(null)
   writebackStatusFilterText: string = 'All Statuses'

   public sites = computed(() => {
      const uniqueSites = new Map<string, string>()
      this.prices()
         ?.filter((price) => !!price.siteSKey)
         .forEach((price) => uniqueSites.set(price.siteName, price.siteName))
      return [{ label: 'All Sites', value: '' }, ...Array.from(uniqueSites.entries()).map(([value, label]) => ({ label, value }))]
   })

   public productCategories = computed(() => {
      if (!this.topLevelRows()) return []

      const uniqueCategories = Array.from(new Set(this.topLevelRows().map((group) => group.pimsCategory)))
      const categories = [
         { label: 'All PIMS Categories', value: '' },
         ...uniqueCategories.map((category) => ({
            label: category,
            value: category,
         })),
      ]

      return categories
   })

   public uniquePractices = computed(() => {
      const uniquePractices = Array.from(new Set(this.topLevelRows()?.map((group) => group.practiceName)))
      return [
         { label: 'All Practices', value: '' },
         ...uniquePractices
            .map((practiceName) => ({
               label: practiceName,
               value: practiceName,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
      ]
   })

   priceChangeData: any[] = []

   @ViewChild('chart') chart!: ChartComponent

   public productTypeOptions: Array<{ label: string; value: string | null }> = [
      { label: 'All Types', value: '' },
      { label: 'Product', value: '0' },
      { label: 'Service', value: '1' },
      { label: 'System Fee', value: 'SF' },
      { label: 'Bundle', value: 'bundle' },
   ]

   @HostBinding('class.full-flex-height') fullFlexHeight = true

   public rejectPriceDialogVisible: boolean = false
   public rejectComment: string = ''
   public rejectProduct: any

   @ViewChild('newCodeRequestAdmin') newCodeRequestAdmin: VetcorPricingNewCodeAdminComponent
   public newCodeRequestId = 0

   detailsModalRef: BsModalRef

   @ViewChildren(VetcorPricingRowComponent)
   rows!: QueryList<VetcorPricingRowComponent>

   constructor(
      private store: VetcorPricingTableComponentStore,
      public toastr: ToastrService,
      private cdr: ChangeDetectorRef,
      private modalService: BsModalService,
      private pricingTableService: VetcorPricingRowService
   ) {
      effect(() => {
         if (this.prices() != this.pricingTableService.prices()) {
            this.pricingTableService.setPrices(this.prices())
         }
      })
   }

   ngOnInit(): void {
      if (this.isRequestView) {
         this.sortModel.set({
            active: 'requestedOn',
            direction: 'desc',
         })
      }

      this.store.setState({
         searchText: '',
         searchCode: '',
         selectedCategory: '',
      })
	  
	  console.log(this.prices())
   }

   onRowSave(data: any) {
      this.recordUpdated.emit(data)
   }

   public setWritebackStatusFilter(status: number | null, label: string): void {
      this.writebackStatusFilter.set(status)
      this.writebackStatusFilterText = label
   }

   public filterProducts() {
      let newRows = this.topLevelRows()?.filter((group) => {
         const product = group

         const hasBundle = group.subRows?.some((subProduct) => subProduct.recordType === 'PG')

         const matchesSite = this.siteCount() === 2 || !this.selectedSite() || this.selectedSite() === product.siteName

         const matchesText = !this.codeFilterValue() || product.code?.toLowerCase().includes(this.codeFilterValue().trim().toLowerCase())

         const matchesName = !this.productNameFilter() || product.recordName?.toLowerCase().includes(this.productNameFilter().trim().toLowerCase())

         const matchesProductType =
            this.productTypeFilter() === '' ||
            (this.productTypeFilter() === 'SF'
               ? product.recordType === 'SF'
               : this.productTypeFilter() === 'bundle'
               ? hasBundle
               : (product.isService ? 1 : 0) === parseInt(this.productTypeFilter(), 10))

         const matchesCategory = !this.productCategoryFilter() || product.pimsCategory === this.productCategoryFilter()

         const matchesPractice = !this.productPracticeFilter() || product.practiceName === this.productPracticeFilter()

         const isAnySubrowApprovedOrRejected = group.subRows.some((subRow) => {
            return !!subRow.approvalStatus
         })

         const matchesCheckboxCondition = !this.pendingReview() || group.approvalStatus === 'Pending'

         const matchesStatus = this.writebackStatusFilter() === null || product.status === this.writebackStatusFilter()

         return (
            matchesText &&
            matchesName &&
            matchesProductType &&
            matchesCategory &&
            matchesPractice &&
            matchesSite &&
            matchesCheckboxCondition &&
            matchesStatus
         )
      })

      return newRows
   }

   openDetailsModal({ group, product }): void {
      this.detailsModalRef = this.pricingTableService.openDetails(this.modalService, product, product.changeType === 'Practice', this.isCaptain)
   }

   scrollUp(): void {
      ;(window as any).scrollTo({ top: 0, behavior: 'smooth' })
   }

   getOverrideRec(product) {
      const reviewPrice = product.reviewPrice

      if (!!product.reviewPrice && product.reviewPrice !== 0) {
         return 'N/A'
      }

      if (reviewPrice <= product.lowballPriceFloor) {
         return 'Reject Override'
      } else if (reviewPrice > product.lowballPriceFloor) {
         if (reviewPrice > product.modelPrice) {
            return 'Approve Override'
         } else if (reviewPrice < product.modelPrice && reviewPrice >= 1.05 * product.averageUnitRetail) {
            return 'Approve Override'
         }
      } else if (reviewPrice < product.modelPrice && reviewPrice < 1.05 * product.averageUnitRetail) {
         return 'Reject Override'
      } else {
         return 'N/A'
      }
   }

   getChangeTypeText(ct: ChangeType) {
      switch (ct) {
         case ChangeType.Historical:
            return 'Historical'
         case ChangeType.Manual:
            return 'Manual'
         case ChangeType.Requested:
            return 'Requested'
         case ChangeType.SystemReset:
            return 'System Reset'
         case ChangeType.SystemWriteback:
            return 'System Writeback'
         case ChangeType.AnnualPricing:
            return 'Annual Pricing'
      }
   }

   getProductType(type: number | null, typeR): string {
      if (typeR == 'P') {
         if (type == 0) {
            return 'Product'
         } else if (type == 1) {
            return 'Service'
         } else {
            return 'Product'
         }
      } else if (typeR == 'SF') {
         return 'System Fee'
      } else if (typeR == 'PF') {
         return 'Product Fee'
      } else {
         return 'Other'
      }
   }

   sortData(data: PricingRowVM[]) {
      const sort = this.sortModel()

      if (!sort?.active || sort?.direction === '') {
         return data
      } else {
         return data?.sort((a, b) => {
            let aValue: any = sort.active in a ? a[sort.active] : a?.metrics[sort.active]
            let bValue: any = sort.active in b ? b[sort.active] : b?.metrics[sort.active]

            // Handle case when value is an array (like quantityBreaks)
            if (Array.isArray(aValue)) {
               aValue = aValue[0]
            }

            if (Array.isArray(bValue)) {
               bValue = bValue[0]
            }

            if (aValue == null) return 1
            if (bValue == null) return -1

            if (typeof aValue === 'string' && typeof bValue === 'string') {
               return aValue.localeCompare(bValue) * (sort.direction === 'asc' ? 1 : -1)
            } else if (typeof aValue === 'number' && typeof bValue === 'number') {
               return (aValue - bValue) * (sort.direction === 'asc' ? 1 : -1)
            } else {
               return 0
            }
         })
      }
   }

   public isHidden: boolean[] = [false]
   toggleVisibility(index: number) {
      this.isHidden[index] = !this.isHidden[index]
   }

   trackByFn(item: PricingRowVM): string {
      return item.id
   }

   exportTableToPDF(adminLevel, filteredProducts: any[]) {
      return
      /*
      const doc = new jsPDF()
      doc.setFontSize(8)

      doc.text('2025 Pricing Table Export', 3, 5)
      let columns = []
      let rows = []

      if (adminLevel == 'practice') {
         columns = [
            'Product Code',
            'Product Name',
            'L12M Rev. $',
            'L12M Rev. %',
            '% Trans. L12M',
            'L12M Client Count',
            'Qty Break',
            'Current Price',
            'Model Price',
            'Req. Price',
            'Total Current Fees',
            'Total Current Price',
            this.isSubmitted ? 'Total Req. Price' : 'Total Req. Price',
         ]

         rows =
            filteredProducts?.map((group) => [
               group.product.code || '',
               group.product.recordName || '',
               group.product.l12MRevenue ? `$${group.product.l12MRevenue.toFixed(2)}` : '',
               group.product.pctAnnualRevenue ? `${(group.product.pctAnnualRevenue * 100).toFixed(2)}%` : '',
               group.product.totalCustomerCodePct ? `${(group.product.totalCustomerCodePct * 100).toFixed(2)}%` : '',
               group.product.distinctClientCount || '',
               group.product.quantityBreak || '',
               group.product.currentPrice ? `$${group.product.currentPrice.toFixed(2)}` : '',
               group.product.modelPrice ? `$${group.product.modelPrice.toFixed(2)}` : '',
               group.product.newPrice ? `$${group.product.newPrice.toFixed(2)}` : '',
               group.subFeeRows
                  ? `$${(this.calculateTotalFee(group.subFeeRows) + this.calculateSystemFeeTotal(group.subSystemRows)).toFixed(2)}`
                  : '',
               group.product.currentPrice
                  ? `$${(
                       group.product.currentPrice +
                       this.calculateTotalFee(group.subFeeRows) +
                       this.calculateSystemFeeTotal(group.subSystemRows)
                    ).toFixed(2)}`
                  : '',
               this.isSubmitted
                  ? `$${this.calculateTotalOpsRequestedPrice(group).toFixed(2)}`
                  : group.product.newPrice
                  ? `$${this.calculateTotalRequestedPrice(group).toFixed(2)}`
                  : '',
            ]) || []
      } else if (adminLevel == 'captain') {
         columns = [
            'Product Code',
            'Product Name',
            'L12M Rev. $',
            'L12M Rev. %',
            '% Trans. L12M',
            'L12M Client Count',
            'Qty Break',
            'Current Price',
            'Model Price',
            'Req. Price',
            'Ops Override Price',
            'Total Current Fees',
            'Total Current Price',
            'Total Req. Price',
            'Total Ops Price',
            'Model Price Impact',
            'Final Price Impact',
         ]

         rows =
            filteredProducts?.map((group) => [
               group.product.code || '',
               group.product.recordName || '',
               group.product.l12MRevenue ? `$${group.product.l12MRevenue.toFixed(2)}` : '',
               group.product.pctAnnualRevenue ? `${(group.product.pctAnnualRevenue * 100).toFixed(2)}%` : '',
               group.product.totalCustomerCodePct ? `${(group.product.totalCustomerCodePct * 100).toFixed(2)}%` : '',
               group.product.distinctClientCount || '',
               group.product.quantityBreak || '',
               group.product.currentPrice ? `$${group.product.currentPrice.toFixed(2)}` : '',
               group.product.modelPrice ? `$${group.product.modelPrice.toFixed(2)}` : '',
               group.product.newPrice ? `$${group.product.newPrice.toFixed(2)}` : '',
               group.product.opsPrice ? `$${group.product.opsPrice.toFixed(2)}` : '',
               group.subFeeRows
                  ? `$${(this.calculateTotalFee(group.subFeeRows) + this.calculateSystemFeeTotal(group.subSystemRows)).toFixed(2)}`
                  : '',
               group.product.currentPrice
                  ? `$${(
                       group.product.currentPrice +
                       this.calculateTotalFee(group.subFeeRows) +
                       this.calculateSystemFeeTotal(group.subSystemRows)
                    ).toFixed(2)}`
                  : '',
               group.product.newPrice ? `$${this.calculateTotalRequestedPrice(group).toFixed(2)}` : '',
               group.product.opsPrice ? `$${this.calculateTotalOpsRequestedPrice(group).toFixed(2)}` : '',
               group.product.revenueImpact ? `$${group.product.revenueImpact}` : '',
               group.product.opsPrice ? this.getPriceImpact(group, true) : '',
            ]) || []
      }

      if (rows.length === 0) {
         this.toastr.error('No data available for export.')
         return
      }

      autoTable(doc, {
         head: [columns],
         body: rows,
         startY: 10,
         styles: {
            fontSize: 6,
            overflow: 'linebreak',
         },
         headStyles: {
            fillColor: [1, 169, 120],
            fontSize: 6,
         },
         columnStyles: {
            0: { cellWidth: 12 },
            1: { cellWidth: 20 },
         },
         margin: { top: 5, left: 3, right: 3, bottom: 5 },
      })

      doc.save('pricing-table.pdf')
      this.savedList = true
      this.productListSaved.emit(this.savedList)
      */
   }

   closeValidationModal() {
      this.pricingTableService.showValidationModal.set(false)
   }

   // Validation and helper methods
   isValidRationale(product: any, forOps: boolean): boolean {
      if (forOps) {
         return !(product.opsPrice && (!product.finalComment || !product.finalComment.trim()))
      } else {
         return !(product.newPrice && (!product.rationale || !product.rationale.trim()))
      }
   }

   isValidPrice(value: any): boolean {
      const regex = /^\d+(\.\d+)?$/
      return regex.test(value?.toString())
   }

   getFormattedPct(value: number): string {
      return (value * 100).toFixed(2) + '%'
   }

   toggleEdit(product: any, event?: Event): void {
      if (event) {
         event.stopPropagation()
      }
      product.isEditing = !product.isEditing
      product.zoho = undefined
   }

   toggleEditAndBeginDiscussion(product: any, event?: Event): void {
      if (event) {
         event.stopPropagation()
      }
      product.isEditing = !product.isEditing
      product.zoho = true
   }

   cancelEdit(product: any, event?: Event): void {
      if (event) {
         event.stopPropagation()
      }
      const editablePriceField = this.isCaptain ? 'opsPrice' : 'newPrice'
      const editableRationaleField = this.isCaptain ? 'finalComment' : 'rationale'
      product[editablePriceField] = null
      product[editableRationaleField] = ''
      this.toggleEdit(product)
   }

   onInputClick(event: Event): void {
      event.stopPropagation()
   }

   findFirstInvalidInputAndFocus(): void {
      for (const row of this.rows.toArray()) {
         const invalidEntry = row.focusInvalidField()
         if (invalidEntry) {
            this.toastr.error(invalidEntry)
            return
         }
      }
   }

   areAllInputsValid(): boolean {
      const editablePriceField = this.isCaptain ? 'opsPrice' : 'newPrice'
      const editableReasonField = this.isCaptain ? 'finalComment' : 'rationale'

      return this.filteredProducts.every((group) => {
         const productValid = !(group.product[editablePriceField] && group.product[editableReasonField] === '')

         const productsValid =
            group.products?.every((product) => {
               return !(product[editablePriceField] && product[editableReasonField] === '')
            }) ?? true

         const subQbRowsValid =
            group.subQbRows?.every((qb) => {
               return !(qb[editablePriceField] && qb[editableReasonField] === '')
            }) ?? true

         const subMinRowsValid =
            group.subMinRows?.every((subMin) => {
               return !(subMin[editablePriceField] && subMin[editableReasonField] === '')
            }) ?? true

         const subBundleRowsValid =
            group.subBundleRows?.every((subBundle) => {
               return !(subBundle[editablePriceField] && subBundle[editableReasonField] === '')
            }) ?? true

         const subFeeRowsValid =
            group.subFeeRows?.every((subFee) => {
               return !(subFee[editablePriceField] && subFee[editableReasonField] === '')
            }) ?? true

         return productValid && productsValid && subQbRowsValid && subMinRowsValid && subBundleRowsValid && subFeeRowsValid
      })
   }
}
